import { useEffect } from "react";
import "./index.css";
import { natsClient } from "./services/NATSClient";
import { UserInitializer } from "./components/UserInitializer";
import { useAppRefresh } from "./hooks/useAppRefresh";
import { styles } from "./styles";

import HomeView from "./views/HomeView";
import LineView from "./views/LineView";
import MachineView from "./views/MachineView";
import Header from "./components/Header";
import NotFoundView from "./views/NotFoundView";
import DateRangePicker from "./components/DateRangePicker";
import AuthGuard from "./components/AuthGuard";
import SAPDataProvider from "./providers/SAPDataProvider";
import { ErrorBoundary } from "@digitalisation/enlixy-comp";
import { BrandingInitializer } from "./components/BrandingInitializer";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSAPDataStore } from "./store/SAPDataStore";
import CompareMachineView from "./views/CompareMachineView";
import CockpitSessionsView from "./views/CockpitSessionsView";
import { CitationViewerHost } from "./components/cockpit/CitationList";
import { useCloseCitationOnLeave } from "./components/cockpit/citationViewerStore";
import { useAssistantUi } from "./components/cockpit/assistant/assistantUiStore";
import { ChatBubble } from "./components/cockpit/ChatBubble";
import DemoModeBadge from "./components/DemoModeBadge";
import WhatsNewPrefetcher from "./components/home/whatsNew/WhatsNewPrefetcher";
import { CockpitAssistant } from "./components/cockpit/assistant/CockpitAssistant";
import { CockpitTourProvider, HelpRoute, Onboarding } from "./help/Onboarding";
import { track } from "./help/learning";

function AppRoutes({
  setDatePickerOpen,
  isDatePickerOpen,
  dateRange,
  setDateRange,
}: {
  setDatePickerOpen: (open: boolean) => void;
  isDatePickerOpen: boolean;
  dateRange: { start: string; end: string };
  setDateRange: (start: string, end: string) => void;
}) {
  const location = useLocation();
  const state = location.state || {};
  const { machines, lines } = useSAPDataStore();
  // The source dialog of AI answers closes with the assistant drawer, on a
  // conversation switch and on navigation.
  const chatOpen = useAssistantUi((s) => s.isOpen);
  const activeSessionId = useAssistantUi((s) => s.sessionId);
  useCloseCitationOnLeave(chatOpen, activeSessionId, location.pathname);

  // Extract machineId from the URL for machine routes
  const machineIdFromPath = location.pathname.startsWith("/machine/")
    ? location.pathname.split("/machine/")[1]
    : null;

  // Extract lineId from the URL for line routes
  const lineIdFromPath = location.pathname.startsWith("/line/")
    ? location.pathname.split("/line/")[1]
    : null;

  // Get machine data from SAP store if on machine route
  const machineData = machineIdFromPath
    ? (() => {
        const machine = machines.find(
          (machine) =>
            machine.maschinennummer === machineIdFromPath ||
            machine.maschineBK === machineIdFromPath ||
            machine.machineId === machineIdFromPath,
        );

        if (machine) {
          // Find the line name from lineId
          const line = lines.find(
            (line) =>
              line.linieBK === machine.lineId || line.lineId === machine.lineId,
          );
          const lineName = line?.linieName;
          const lineNumber = line?.linieNummer;

          // Map the machine data to the format expected by Header
          return {
            machineId: machine.maschinennummer || machine.maschineBK, // Use maschinennummer as the primary ID
            machineName: machine.machineName || machine.bautyp,
            lineId: machine.lineId,
            lineName: lineName,
            lineNumber: lineNumber,
            machineType: machine.bautyp || machine.machineType,
            factory: machine.factoryName,
          };
        }

        return state; // Fallback to route state if machine not found in store
      })()
    : undefined;

  // Get line data from SAP store if on line route
  const lineData = lineIdFromPath
    ? (() => {
        const line = lines.find(
          (line) =>
            line.lineId === lineIdFromPath || line.linieBK === lineIdFromPath,
        );
        if (line) {
          return {
            lineId: line.lineId || line.linieBK,
            lineNumber: line.linieNummer,
            lineName: line.linieName,
            factory: line.factoryName,
            machineCount: line.maschinen?.length || 0,
            machines: line.maschinen || [],
            // OEM number is on the line (not on machines)
            oemNumbers: line.oemNummer ? [line.oemNummer] : [],
          };
        }
        return state;
      })()
    : undefined;

  // Get machine data for DateRangePicker if on machine view
  const machineDataForPicker =
    machineIdFromPath && machines && Array.isArray(machines)
      ? (() => {
          try {
            const machine = machines.find(
              (machine) =>
                machine &&
                (machine.maschinennummer === machineIdFromPath ||
                  machine.maschineBK === machineIdFromPath ||
                  machine.machineId === machineIdFromPath),
            );

            return machine
              ? {
                  machineId: machine.maschinennummer || machine.maschineBK,
                  machineName: machine.machineName || machine.bautyp,
                  serialno: machine.maschinennummer || machine.maschineBK, // Use as serialno for NATS request
                }
              : undefined;
          } catch (error) {
            console.error("Error finding machine data for picker:", error);
            return undefined;
          }
        })()
      : undefined;

  // For Header navigation, provide a navigate function via context or window for legacy components
  (window as any).reactRouterNavigate = useNavigate();

  return (
    <>
      <Header
        currentView={location.pathname.split("/")[1] || "home"}
        machineData={machineData}
        lineData={lineData}
      />

      {/* Modal Date Range Picker inside Router context */}
      {isDatePickerOpen && (
        <div
          className={styles.app.modal.overlay}
          aria-modal="true"
          role="dialog"
          onClick={() => setDatePickerOpen(false)}
        >
          <div
            className={styles.app.modal.container}
            style={{ pointerEvents: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <DateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              machineData={machineDataForPicker}
              onChange={(start, end, action) => {
                if (action === "apply") {
                  setDateRange(start, end);
                  track("range_changed");
                  setDatePickerOpen(false);
                } else if (action === "cancel") {
                  setDatePickerOpen(false);
                }
                // Do not close modal or update range on other actions
              }}
            />
          </div>
        </div>
      )}

      {/* A page that fails to render shows the shared error card (header,
          sidebar and assistant stay); going to another page clears it. */}
      {/* lg:pl-18: the pages start right of the menu's icon rail */}
      <div className="lg:pl-18">
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/" element={<HomeView />} />
          <Route path="/line/:lineId" element={<LineView />} />
          <Route path="/machine/:machineId" element={<MachineView />} />
          <Route path="/compare-machine" element={<CompareMachineView />} />
          {/* the former settings page: its demo switch is in the avatar menu */}
          <Route path="/settings" element={<Navigate to="/" replace />} />
          <Route path="/cockpit/sessions" element={<CockpitSessionsView />} />
          {/* the former help page: the handbook of Help & learning */}
          <Route path="/help" element={<HelpRoute />} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </ErrorBoundary>
      </div>

      {/* KI-Assistent: floating action button + the shared assistant drawer */}
      <ChatBubble />
      <CockpitAssistant />
      {/* Source dialog of AI answers (one for the drawer and the sessions page). */}
      <CitationViewerHost />

      {/* Permanent reminder when the app is pointed at the demo dataset. */}
      <DemoModeBadge />

      {/*
        Builds the daily What's New briefing in the background, so it's ready
        before the user opens the tab. Renders nothing; the once-per-day marker in
        NATS KV decides whether any work actually happens.
      */}
      <WhatsNewPrefetcher />

      {/* Help & learning: learning path, guided tours, handbook (F1) */}
      <Onboarding />
    </>
  );
}

function App() {
  const { dateRange, isDatePickerOpen, setDateRange, setDatePickerOpen } =
    useSAPDataStore();

  // Initialize automatic refresh strategies
  useAppRefresh();

  // NATS connection lifecycle is managed by SAPDataProvider
  // Disconnect only on actual page unload, not React unmount (Strict Mode double-fires cleanups)
  useEffect(() => {
    const handleUnload = () => natsClient.disconnect();
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    if (isDatePickerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDatePickerOpen]);

  return (
    <AuthGuard>
      <UserInitializer />
      <BrandingInitializer />
      <div className={styles.app.container}>
        <div aria-hidden={isDatePickerOpen ? "true" : undefined}>
          <BrowserRouter>
            <SAPDataProvider>
              <CockpitTourProvider>
                <AppRoutes
                  setDatePickerOpen={setDatePickerOpen}
                  isDatePickerOpen={isDatePickerOpen}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
              </CockpitTourProvider>
            </SAPDataProvider>
          </BrowserRouter>
        </div>
      </div>
    </AuthGuard>
  );
}

export default App;
